import {
    IndexedDBService,
    LockManager,
    TabManagerService,
    UUIDService,
} from '@pulse/browser-shared';
import {
    BatchInQueue,
    PulseBatch,
    PulseBatchConfig,
    PulseMessage,
    PulseStorage,
    TrackerConfigurations,
    sleep,
} from '@pulse/common';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import {
    PageLoadedService,
    PulseTransport,
    ResolveWith,
    TabInfoService,
    TrackingConsentService,
} from '../services';
import { getBatches, getTrackerBatchConfig } from './batch-helper';

type StoredBatchRecord = {
    id: string;
    batch: PulseBatch;
    time: number;
    tabId?: string;
};

const BATCH_MAX_AGE_MS = 5 * 24 * 60 * 60 * 1000; // 5 days
const PROCESSED_BATCH_ID_TTL_MS = 5000;

export class BatchQueue {
    private batchQueue: BatchInQueue[] = [];
    private sendingBatches: BatchInQueue[] = [];
    private processedBatchIds = new Set<string>();
    private trackingEnabled?: boolean;
    private currentRequestSize = 0;
    private beaconLimit = 60_000;
    private queueProcessor: ReturnType<typeof setInterval> | null = null;
    private STORED_BATECHES_KEY = 'pulse-unpublished-events';
    private batchConfig: PulseBatchConfig;
    private pageLoaded = false;
    readonly queueEmpty$ = new Subject<boolean>();

    constructor(
        private storage: PulseStorage,
        private lockManager: LockManager,
        private uuid: UUIDService,
        private consentService: TrackingConsentService,
        private transport: PulseTransport,
        private config: TrackerConfigurations,
        private readonly indexedDB: IndexedDBService,
        private readonly tabManager: TabManagerService,
        private readonly pageLoadedService: PageLoadedService,
        private readonly tabInfoService: TabInfoService,
    ) {
        if (this.trackingEnabled) {
            this.queueStoredMessagesIfAny();
        }

        this.consentService.trackingEnabled().subscribe((enabled) => {
            this.trackingEnabled = enabled;
            this.batchConfig = getTrackerBatchConfig(this.config, this.trackingEnabled);
        });

        this.batchConfig = getTrackerBatchConfig(this.config, this.trackingEnabled);

        this.cleanupOldBatchesFromIndexedDB();
        this.rehydrateBatchesFromIndexedDB();

        this.pageLoadedService
            .whenLoaded()
            .pipe(take(1))
            .subscribe(() => {
                this.pageLoaded = true;
                this.startQueue();
            });
    }

    pushBatch(batch: PulseBatch) {
        const batchId = this.uuid.generate();
        this.removeEventsInBatchFromIndexedDB(batch);
        this.persistBatchToIndexedDB(batchId, batch);
        this.processedBatchIds.add(batchId);
        this.batchQueue.push({ batch, batchId });

        if (this.pageLoaded && !this.queueProcessor) {
            this.controlQueueProcessor();
        }
    }

    private removeEventsInBatchFromIndexedDB(batch: PulseBatch): void {
        if (batch.chunks.length === 0) {
            return;
        }

        const idsToDelete: string[] = [];
        for (const chunk of batch.chunks) {
            for (const e of chunk.events) {
                const id = e.event_id;

                if (id !== undefined && id !== null && id !== '') {
                    idsToDelete.push(id);
                }
            }

            for (const attr of chunk.attributes) {
                const id = (attr as { id?: string }).id;

                if (id !== undefined && id !== null && id !== '') {
                    idsToDelete.push(id);
                }
            }

            if (chunk.attributeHints) {
                const id = (chunk.attributeHints as { id?: string }).id;

                if (id !== undefined && id !== null && id !== '') {
                    idsToDelete.push(id);
                }
            }
        }

        if (idsToDelete.length === 0) {
            return;
        }

        this.indexedDB
            .runInTransaction(IndexedDBService.EVENTS_STORE, 'readwrite', (tx) => {
                const store = tx.objectStore(IndexedDBService.EVENTS_STORE);
                idsToDelete.forEach((id) => store.delete(id));
            })
            .catch(() => undefined);
    }

    private persistBatchToIndexedDB(batchId: string, batch: PulseBatch): void {
        let tabId: string | undefined;
        try {
            tabId = this.tabManager.getTabId();
        } catch {
            // Tab may not be initialized yet
        }
        this.indexedDB
            .putValue(IndexedDBService.BATCHES_STORE, {
                id: batchId,
                batch: { ...batch },
                time: Date.now(),
                tabId,
            })
            .catch(() => undefined);
    }

    private cleanupOldBatchesFromIndexedDB(): void {
        const cutoff = Date.now() - BATCH_MAX_AGE_MS;
        this.indexedDB
            .getAllWithTimeBefore<StoredBatchRecord>(IndexedDBService.BATCHES_STORE, cutoff)
            .then((oldRecords) => {
                if (oldRecords.length === 0) {
                    return;
                }

                return this.indexedDB.runInTransaction(
                    IndexedDBService.BATCHES_STORE,
                    'readwrite',
                    (tx) => {
                        const store = tx.objectStore(IndexedDBService.BATCHES_STORE);
                        oldRecords.forEach((record) => store.delete(record.id));
                    },
                );
            })
            .catch(() => undefined);
    }

    private async rehydrateBatchesFromIndexedDB(): Promise<void> {
        await sleep(1000); // Wait for any potential sibling tab to finish delivering its final accumulated events before withdrawing event processing
        this.indexedDB
            .getAllValue<StoredBatchRecord[]>(IndexedDBService.BATCHES_STORE)
            .then((records) => {
                if (!Array.isArray(records) || records.length === 0) {
                    return;
                }
                const openSiblingTabIds = this.tabInfoService.getOpenSiblingTabIds();
                records.forEach((record) => {
                    if (!record?.batch || this.processedBatchIds.has(record.id)) {
                        return;
                    }
                    if (this.batchQueue.some((b) => b.batchId === record.id)) {
                        return;
                    }
                    if (
                        record.tabId !== undefined &&
                        record.tabId !== null &&
                        openSiblingTabIds.includes(record.tabId)
                    ) {
                        return;
                    }
                    this.processedBatchIds.add(record.id);
                    this.batchQueue.push({
                        batch: record.batch,
                        batchId: record.id,
                    });
                });
                if (this.pageLoaded && this.batchQueue.length && !this.queueProcessor) {
                    this.controlQueueProcessor();
                }
            })
            .catch(() => undefined);
    }

    startQueue() {
        if (this.batchQueue.length && !this.queueProcessor) {
            this.controlQueueProcessor();
        }
    }

    getQueueEmpty$(): Subject<boolean> {
        return this.queueEmpty$;
    }

    private async queueStoredMessagesIfAny() {
        const locked = await this.lockManager.locked(this.STORED_BATECHES_KEY);

        if (locked) {
            return;
        }

        const lock = await this.lockManager.request(this.STORED_BATECHES_KEY, 6_000);

        if (!lock) {
            return;
        }

        const storedBatches =
            this.storage.getJSON<PulseMessage[] | BatchInQueue[]>(this.STORED_BATECHES_KEY) || [];

        this.storage.remove(this.STORED_BATECHES_KEY);
        this.lockManager.release(lock);

        if (!storedBatches.length) {
            return;
        }

        if ('batchId' in storedBatches[0]) {
            (storedBatches as BatchInQueue[]).forEach((batch) => {
                if (
                    !this.processedBatchIds.has(batch.batchId) &&
                    !this.batchQueue.some((queuedBatch) => queuedBatch.batchId === batch.batchId)
                ) {
                    this.processedBatchIds.add(batch.batchId);
                    this.batchQueue.push(batch);
                }
            });
        } else {
            const batches = getBatches(storedBatches as PulseMessage[], this.batchConfig);
            batches.forEach((batch) => this.pushBatch(batch));
        }
        if (this.pageLoaded && this.batchQueue.length && !this.queueProcessor) {
            this.controlQueueProcessor();
        }
    }

    private controlQueueProcessor() {
        if (!this.queueProcessor) {
            this.queueProcessor = setInterval(() => {
                if (!this.batchQueue.length && !this.sendingBatches.length) {
                    this.queueEmpty$.next(true);
                    this.currentRequestSize = 0;
                }
                if (this.batchQueue.length === 0 && this.queueProcessor) {
                    this.stopQueueProcessor();

                    return;
                }
                const currentBatchSize = this.batchQueue[0].batch.size || 0;

                if (
                    !this.currentRequestSize ||
                    this.currentRequestSize + currentBatchSize < this.beaconLimit
                ) {
                    this.currentRequestSize += currentBatchSize;
                    const batch = this.batchQueue.shift();

                    if (batch) {
                        this.sendingBatches.push(batch);
                        this.sendBatch(batch.batch, batch.batchId)
                            .then((res) => this.handleResponse(res))
                            .catch((err) => console.error(err));
                    }
                    if (this.batchQueue.length === 0 && this.queueProcessor) {
                        if (!this.sendingBatches.length) {
                            this.queueEmpty$.next(true);
                        }

                        this.stopQueueProcessor();
                        this.queueProcessor = null;
                    }
                }
            }, 100);
        }
    }

    private stopQueueProcessor() {
        if (this.queueProcessor) {
            clearInterval(this.queueProcessor);
            this.queueProcessor = null;
        }
    }

    private handleResponse({ size, batchId }: ResolveWith) {
        this.currentRequestSize -= size;
        const index = this.sendingBatches.findIndex((batch) => batch.batchId === batchId);

        if (index !== -1) {
            this.sendingBatches.splice(index, 1);
        }
        this.removeBatchFromIndexedDB(batchId);
        this.scheduleProcessedBatchIdRemoval(batchId);
        if (!this.sendingBatches.length && !this.batchQueue.length) {
            this.queueEmpty$.next(true);
        }
    }

    private scheduleProcessedBatchIdRemoval(batchId: string): void {
        setTimeout(() => {
            this.processedBatchIds.delete(batchId);
        }, PROCESSED_BATCH_ID_TTL_MS);
    }

    private removeBatchFromIndexedDB(batchId: string): void {
        this.indexedDB.deleteValue(IndexedDBService.BATCHES_STORE, batchId).catch(() => undefined);
    }

    private sendBatch(batch: PulseBatch, batchId: string): Promise<ResolveWith> {
        return new Promise<ResolveWith>((resolve) => {
            const keepalive = Boolean(batch.keepAlive);
            delete batch.keepAlive;
            this.transport.send(batch, keepalive, resolve, batchId);
        });
    }
}
